/**
 * Chat API Service
 *
 * Frontend API methods for the AI chatbot. Uses the standard axios client
 * for REST endpoints and raw fetch for the SSE streaming message endpoint.
 */

import { api } from '../utils/api';
import { STORAGE_KEYS } from '@/config/constants';

export interface Conversation {
  id: string;
  title: string | null;
  lastMessageAt: string;
  createdAt: string;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'tool_use' | 'tool_result';
  content: string | null;
  toolName: string | null;
  toolInput: Record<string, unknown> | null;
  toolResult: Record<string, unknown> | null;
  metadata: Record<string, unknown> | null;
  createdAt: string;
}

export interface ConversationWithMessages extends Conversation {
  messages: ChatMessage[];
}

export interface SSEEvent {
  type: 'token' | 'tool_start' | 'tool_result' | 'done' | 'error';
  content?: string;
  tool?: string;
  toolInput?: Record<string, unknown>;
  toolResult?: Record<string, unknown>;
  metadata?: { links: Array<{ label: string; path: string }> };
}

export interface PageContext {
  path: string;
  entityType?: 'opportunity' | 'quote' | 'proposal' | 'customerAccount';
  entityId?: string;
}

const ChatService = {
  /**
   * Transcribe audio via Whisper.
   */
  transcribeAudio: async (audioBlob: Blob): Promise<{ text: string; language?: string }> => {
    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.webm');

    const response = await api.post('/chat/transcribe', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data.data;
  },

  createConversation: async (): Promise<{ id: string }> => {
    const response = await api.post('/chat/conversations');
    return response.data.data;
  },

  listConversations: async (): Promise<Conversation[]> => {
    const response = await api.get('/chat/conversations');
    return response.data.data;
  },

  getConversation: async (id: string): Promise<ConversationWithMessages> => {
    const response = await api.get(`/chat/conversations/${id}`);
    return response.data.data;
  },

  deleteConversation: async (id: string): Promise<void> => {
    await api.delete(`/chat/conversations/${id}`);
  },

  /**
   * Send a message and receive SSE events. Uses raw fetch instead of axios
   * to handle the streaming response via ReadableStream.
   *
   * @param conversationId - Conversation UUID
   * @param content        - User message text
   * @param onEvent        - Callback for each SSE event
   * @param signal         - Optional AbortSignal for cancellation
   * @param pageContext    - Optional current page context (path, entityType, entityId)
   */
  sendMessage: async (
    conversationId: string,
    content: string,
    onEvent: (event: SSEEvent) => void,
    signal?: AbortSignal,
    pageContext?: PageContext
  ): Promise<void> => {
    const baseUrl = import.meta.env.VITE_API_URL || '/api/v1';
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);

    const response = await fetch(`${baseUrl}/chat/conversations/${conversationId}/messages`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ content, pageContext: pageContext ?? undefined }),
      signal,
    });

    if (!response.ok) {
      throw new Error(`Chat request failed: ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let buffer = '';
    let streamDone = false;

    while (!streamDone) {
      const { done, value } = await reader.read();
      if (done) {
        streamDone = true;
        continue;
      }

      buffer += decoder.decode(value, { stream: true });

      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();
        if (trimmed.startsWith('data: ')) {
          try {
            const data = JSON.parse(trimmed.slice(6));
            onEvent(data);
          } catch {
            // skip malformed events
          }
        }
      }
    }

    if (buffer.trim().startsWith('data: ')) {
      try {
        const data = JSON.parse(buffer.trim().slice(6));
        onEvent(data);
      } catch {
        // skip
      }
    }
  },
};

export default ChatService;
